import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  MapPin, 
  Star, 
  Clock, 
  Phone, 
  Navigation, 
  Eye,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  X
} from 'lucide-react';
import { fetchNearbyShops, createClaimRequest } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const FindMyShop = ({ onBack }) => {
  const [shops, setShops] = useState([]);
  const [filteredShops, setFilteredShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('distance');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [selectedShop, setSelectedShop] = useState(null);
  const [showShopDetails, setShowShopDetails] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({ name: '', email: '', mobile: '', amount: '', businessEmail: '' });
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const categories = [
    'All', 
    'Coffee', 
    'Restaurant', 
    'Retail', 
    'Grocery', 
    'Beauty', 
    'Fitness'
  ];

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    filterAndSortShops();
  }, [shops, searchTerm, selectedCategory, sortBy, sortDirection]);

  const fetchShops = async () => {
    try {
      setLoading(true);
      const response = await fetchNearbyShops({});
      const shopsData = response?.extractedData || response?.data || response || [];
      
      // Transform API data to match our needs
      const transformedShops = (Array.isArray(shopsData) ? shopsData : []).map((shop, index) => ({
        id: shop.profile_id || shop.id || index,
        name: shop.shopDetails?.[0]?.shopName || shop.firstName + "'s Shop" || `Shop ${index + 1}`,
        category: shop.shopDetails?.[0]?.category || categories[Math.floor(Math.random() * (categories.length - 1)) + 1],
        rating: shop.shopDetails?.[0]?.rating || (Math.random() * 2 + 3).toFixed(1),
        reviewCount: Math.floor(Math.random() * 200) + 10,
        address: shop.address || "123 Main Street, City",
        phone: shop.phone || shop.shopDetails?.[0]?.mobile || "+91 98765 43210",
        hours: shop.shopDetails?.[0]?.hours || "9 AM - 9 PM",
        photo: shop.shopDetails?.[0]?.frontPhoto || `https://picsum.photos/400/300?random=${index + 1}`,
        distance: shop.shopDetails?.[0]?.[" distance"] || `${(Math.random() * 5 + 0.1).toFixed(1)} km`,
        rewardRate: shop.shopDetails?.[0]?.specialDiscount || "10% cashback",
        isOpen: Math.random() > 0.3, // 70% chance of being open
        location: shop.shopDetails?.[0]?.location || { lat: 13.0827, lng: 80.2707 },
        offers: shop.shopDetails?.[0]?.offers || []
      }));

      setShops(transformedShops);
    } catch (error) {
      console.error('Error fetching shops:', error);
      toast.error('Failed to load nearby shops');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortShops = () => {
    let filtered = [...shops];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(shop =>
        shop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shop.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shop.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(shop => shop.category === selectedCategory);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'distance':
          aValue = parseFloat(a.distance);
          bValue = parseFloat(b.distance);
          break;
        case 'rating':
          aValue = parseFloat(a.rating);
          bValue = parseFloat(b.rating);
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        default:
          aValue = parseFloat(a.distance);
          bValue = parseFloat(b.distance);
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredShops(filtered);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const handleDirections = (shop) => {
    const { lat, lng } = shop.location;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, '_blank');
  };

  const handleViewShop = (shop) => {
    // Show shop details modal or component
    setSelectedShop(shop);
    setShowShopDetails(true);
  };

  const openPurchaseModal = (shop) => {
    setSelectedShop(shop);
    setPurchaseForm(prev => ({
      ...prev,
      name: prev.name || '',
      email: currentUser?.email || prev.email || '',
      mobile: prev.mobile || '',
      amount: '',
      businessEmail: prev.businessEmail || ''
    }));
    setShowPurchaseModal(true);
  };

  const submitPurchase = async (e) => {
    e.preventDefault();
    try {
      const { name, email, mobile, amount, businessEmail } = purchaseForm;
      if (!name || !email || !mobile || !amount || !businessEmail) {
        toast.error('Please fill all fields');
        return;
      }
      await createClaimRequest({
        customerName: name,
        customerEmail: email,
        customerMobile: mobile,
        amount: Number(amount) || 0,
        businessEmail,
        status: 'pending',
        requestDate: new Date().toISOString(),
        shopName: selectedShop?.name || ''
      });
      toast.success('Purchase sent to shop. Points will be credited shortly.');
      setShowPurchaseModal(false);
      setPurchaseForm({ name: '', email: currentUser?.email || '', mobile: '', amount: '', businessEmail: '' });
    } catch (err) {
      console.error('submitPurchase failed', err);
      toast.error('Failed to send purchase');
    }
  };

  const handleBack = () => {
    navigate('/customer/dashboard');
  };
  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredShops.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedShops = filteredShops.slice(startIndex, startIndex + itemsPerPage);

  const ShopDetailsModal = ({ shop, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="relative">
          {/* Shop Image */}
          <div className="h-64 overflow-hidden rounded-t-xl">
            <img
              src={shop.photo}
              alt={shop.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = `https://picsum.photos/600/300?random=${shop.id}`;
              }}
            />
            <div className="absolute top-4 right-4">
              <button
                onClick={onClose}
                className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-2 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Shop Details */}
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{shop.name}</h2>
                <div className="flex items-center space-x-2 mb-2">
                  <Star className="w-5 h-5 text-yellow-400 fill-current" />
                  <span className="font-medium text-gray-700">{shop.rating}</span>
                  <span className="text-gray-500">({shop.reviewCount} reviews)</span>
                </div>
              </div>
              <div className="flex flex-col items-end space-y-2">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {shop.category}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  shop.isOpen 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {shop.isOpen ? 'Open' : 'Closed'}
                </span>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">{shop.address}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">{shop.phone}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">{shop.hours}</span>
              </div>
            </div>

            {/* Reward Information */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-green-800 mb-2">🎁 Rewards & Offers</h3>
              <p className="text-green-700">{shop.rewardRate}</p>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => handleDirections(shop)}
                className="flex-1 flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Navigation className="w-5 h-5" />
                <span>Get Directions</span>
              </button>
              <button
                onClick={() => {
                  // Add to favorites or other action
                  toast.success(`Added ${shop.name} to favorites!`);
                }}
                className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                ❤️ Favorite
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ShopCard = ({ shop }) => (
    <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden">
      {/* Shop Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={shop.photo}
          alt={shop.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = `https://picsum.photos/400/300?random=${shop.id}`;
          }}
        />
        
        {/* Category Badge */}
        <div className="absolute top-3 right-3">
          <span className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-medium">
            {shop.category}
          </span>
        </div>
        
        {/* Open/Closed Status */}
        <div className="absolute bottom-3 left-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            shop.isOpen 
              ? 'bg-green-100 text-green-800 border border-green-200' 
              : 'bg-red-100 text-red-800 border border-red-200'
          }`}>
            {shop.isOpen ? 'Open' : 'Closed'}
          </span>
        </div>
        
        {/* Distance */}
        <div className="absolute bottom-3 right-3 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
          {shop.distance}
        </div>
      </div>

      {/* Shop Details */}
      <div className="p-4">
        {/* Name and Rating */}
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-lg text-gray-900 truncate flex-1 mr-2">
            {shop.name}
          </h3>
          <div className="flex items-center space-x-1 flex-shrink-0">
            <Star className="w-4 h-4 text-yellow-400 fill-current" />
            <span className="text-sm font-medium text-gray-700">{shop.rating}</span>
            <span className="text-xs text-gray-500">({shop.reviewCount})</span>
          </div>
        </div>

        {/* Address */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
          <MapPin className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">{shop.address}</span>
        </div>

        {/* Hours */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span>{shop.hours}</span>
        </div>

        {/* Phone */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-3">
          <Phone className="w-4 h-4 flex-shrink-0" />
          <span>{shop.phone}</span>
        </div>

        {/* Reward Rate */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-4">
          <p className="text-sm font-medium text-green-800">🎁 {shop.rewardRate}</p>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <button
            onClick={() => handleDirections(shop)}
            className="flex-1 flex items-center justify-center space-x-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <Navigation className="w-4 h-4" />
            <span>Directions</span>
          </button>
          {/* <button
            onClick={() => openPurchaseModal(shop)}
            className="flex-1 flex items-center justify-center space-x-1 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
          >
            <span>Purchase</span>
          </button> */}
          <button
            onClick={() => handleViewShop(shop)}
            className="flex-1 flex items-center justify-center space-x-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
          >
            <Eye className="w-4 h-4" />
            <span>View Shop</span>
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading nearby shops...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Find My Shop</h1>
              <p className="text-gray-600 mt-1">Discover nearby shops and their rewards</p>
            </div>
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          {/* Search Bar */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search shops by name, category, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Sort Options */}
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Sort by:</span>
            <button
              onClick={() => handleSort('distance')}
              className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-sm transition-colors ${
                sortBy === 'distance' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span>Distance</span>
              {sortBy === 'distance' && <ArrowUpDown className="w-3 h-3" />}
            </button>
            <button
              onClick={() => handleSort('rating')}
              className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-sm transition-colors ${
                sortBy === 'rating' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span>Rating</span>
              {sortBy === 'rating' && <ArrowUpDown className="w-3 h-3" />}
            </button>
            <button
              onClick={() => handleSort('name')}
              className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-sm transition-colors ${
                sortBy === 'name' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span>Name</span>
              {sortBy === 'name' && <ArrowUpDown className="w-3 h-3" />}
            </button>
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-gray-600">
            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredShops.length)} of {filteredShops.length} shops
            {selectedCategory !== 'All' && ` in ${selectedCategory}`}
          </p>
        </div>

        {/* Shop Grid */}
        {paginatedShops.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
            {paginatedShops.map((shop) => (
              <ShopCard key={shop.id} shop={shop} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No shops found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
          </div>
        )}

        {/* Pagination */}
        {filteredShops.length > 0 && (
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-2 rounded-lg ${
                  currentPage === i + 1
                    ? 'bg-blue-600 text-white'
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {i + 1}
              </button>
            ))}
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Shop Details Modal */}
        {showShopDetails && selectedShop && (
          <ShopDetailsModal
            shop={selectedShop}
            onClose={() => {
              setShowShopDetails(false);
              setSelectedShop(null);
            }}
          />
        )}

        {/* Purchase Modal */}
        {showPurchaseModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">Purchase at {selectedShop?.name || 'Shop'}</h3>
                <button onClick={() => setShowPurchaseModal(false)} className="text-gray-400 hover:text-gray-600 p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form className="p-6 space-y-4" onSubmit={submitPurchase}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                  <input
                    type="text"
                    value={purchaseForm.name}
                    onChange={(e)=>setPurchaseForm(p=>({...p,name:e.target.value}))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Email</label>
                  <input
                    type="email"
                    value={purchaseForm.email}
                    onChange={(e)=>setPurchaseForm(p=>({...p,email:e.target.value}))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="you@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Mobile</label>
                  <input
                    type="tel"
                    value={purchaseForm.mobile}
                    onChange={(e)=>setPurchaseForm(p=>({...p,mobile:e.target.value.replace(/[^0-9+]/g,'')}))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="10-digit mobile"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                  <input
                    type="number"
                    min="1"
                    value={purchaseForm.amount}
                    onChange={(e)=>setPurchaseForm(p=>({...p,amount:e.target.value}))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter purchase amount"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shop Business Email</label>
                  <input
                    type="email"
                    value={purchaseForm.businessEmail}
                    onChange={(e)=>setPurchaseForm(p=>({...p,businessEmail:e.target.value}))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="shop@example.com"
                    required
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={()=>setShowPurchaseModal(false)} className="px-4 py-2 rounded-lg border">Cancel</button>
                  <button type="submit" className="px-4 py-2 rounded-lg text-white bg-blue-600 hover:bg-blue-700">Send</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FindMyShop;
